import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'

export interface IdentityPermissions {
    canUpdate: boolean
    canRehire: boolean
    canRevokeAssignment: boolean
    canCreateAccount: boolean
    canUpdateAccount: boolean
    canDeleteAccount: boolean
    canAssignFg: boolean
}

/**
 * Per-control permission gate for the IdentityDetail page.
 *
 * One field per distinct boundary — never aggregate. Each field maps to a
 * specific permission string and gates one cluster of UI controls:
 *
 * - {@code canUpdate}            — lifecycle action buttons (suspend / reactivate /
 *                                  terminate), reactivation-date edit, evaluate rules,
 *                                  attribute editor edit mode, sources card edit.
 * - {@code canRehire}             — rehire button (only meaningful on TERMINATED).
 * - {@code canRevokeAssignment}   — revoke buttons + Actions column on the
 *                                   direct AP assignments table.
 * - {@code canCreateAccount}      — create-account button + dialog and the
 *                                   eligible-systems lookup query.
 * - {@code canUpdateAccount}      — disable / enable account buttons.
 * - {@code canDeleteAccount}      — delete-account button (also covers cancel-
 *                                   creation when status is PENDING).
 * - {@code canAssignFg}           — revoke buttons + Actions column on the
 *                                   function-group assignments table.
 */
export function useIdentityPermissions(): IdentityPermissions {
    const {hasPermission} = useAuth()
    return {
        canUpdate: hasPermission(permissions.identities.update),
        canRehire: hasPermission(permissions.identities.rehire),
        canRevokeAssignment: hasPermission(permissions.assignments.revoke),
        canCreateAccount: hasPermission(permissions.accounts.create),
        canUpdateAccount: hasPermission(permissions.accounts.update),
        canDeleteAccount: hasPermission(permissions.accounts.delete),
        canAssignFg: hasPermission(permissions.functionGroups.assign),
    }
}
